import React, { useEffect, useRef } from 'react';
import { DragSourceMonitor, useDrag, useDrop } from 'react-dnd';

import { getEmptyImage } from 'react-dnd-html5-backend';
import { IPhotoTileProps, PhotoTile } from './PhotoTile';

export interface IDraggablePhotoTileProps {
	moveTile: (id: string, to: number) => void;
	findTile: (id: string) => { index: number };
	onTargetPositionChange: (position: number) => void;
}

export enum DragDropItemType {
	TILE = 'tile'
}

interface IDragDropTile {
	id: string;
	type: DragDropItemType.TILE;
	originalIndex: number;
	tile: IPhotoTileProps;
}

const DraggablePhotoTile: React.FC<IDraggablePhotoTileProps & IPhotoTileProps> = (props) => {
	const {
		id,
		isDisabled,
		moveTile,
		findTile,
		onTargetPositionChange
	} = props;

	// This ref is used by the PhotoTilePreview to copy the width of the grabbed tile
	const ref = useRef(null);

	const originalIndex = findTile(id).index;

	const [{ isDragging }, drag, preview] = useDrag({
		item: { type: DragDropItemType.TILE, id, originalIndex, tile: props, target: ref },
		canDrag: () => !isDisabled,
		collect: (monitor: DragSourceMonitor) => ({ isDragging: monitor.isDragging() }),
		end: () => onTargetPositionChange(0)
	});

	useEffect(() => {
		preview(getEmptyImage(), { captureDraggingState: true });
	}, []);

	const [, drop] = useDrop({
		accept: DragDropItemType.TILE,
		hover: ({ originalIndex: draggedIndex }) => {
			const targetPosition = originalIndex + 1;
			onTargetPositionChange(originalIndex < draggedIndex ? -targetPosition : targetPosition);
		},
		drop: ({ id: draggedId }: IDragDropTile) => {
			if (draggedId !== id) moveTile(draggedId, findTile(id).index);
		},
	});

	// Register that the tile can be dragged, and is a drop target
	drag(drop(ref));

	return <div ref={ref} className={`drag-drop__wrapper ${isDragging ? 'drag-drop__wrapper--dragging' : ''}`}>
		<PhotoTile {...props} isGrabbing={isDragging} />
	</div>;
};

export default DraggablePhotoTile;
